import React from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { Bars3Icon, XMarkIcon, ChartBarIcon, UserCircleIcon, ArrowRightEndOnRectangleIcon, TrophyIcon } from '@heroicons/react/24/outline'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { SecondaryBtn } from './Assets/Buttons'
import { ThemeToggle } from './Assets/ThemeToggle'
import { Logo } from './Assets/Logo'
import { useLogoutMutation } from '../services/api/userApi'

// Helper function to combine classes conditionally - useful for active links
function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function Navbar({ isLoggedIn, isSubscribed }) {
    const navigate = useNavigate()
    const location = useLocation()
    const [logout, { isLoading: isLoggingOut, isError: isLogoutError, error: logoutError }] = useLogoutMutation();
  
    const handleLogout = async() => {
        try {
            await logout();
            navigate('/')
        } catch (error) {
            console.error(error);
        }
    }

    // Function to handle scrolling to sections on the landing page
    const handleHashLink = (e, href) => {
        if (href.startsWith('#')) {
            e.preventDefault();
      
            if (location.pathname !== '/') {
                // If we're not on the landing page, navigate to landing with hash
                navigate('/' + href);
                // After navigation, scroll to element
                setTimeout(() => {
                    const element = document.getElementById(href.substring(1));
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100); // Small delay to ensure DOM is updated
            } else {
                // If we're already on the landing page, just scroll
                const element = document.getElementById(href.substring(1));
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }
        }
    };

    // Define navigation items for easier mapping
    const navigation = [
        { name: 'Games', href: '/upcoming-games', current: location.pathname === '/upcoming-games', requiresAuth: true, requiresSubscription: false, badge: false },
        { name: 'Systems', href: '/systems', current: location.pathname === '/systems', requiresAuth: true, requiresSubscription: false },
        { name: 'FAQ', href: '#faq', current: location.pathname === '/faq', requiresAuth: false, requiresSubscription: false, hiddenIfSubscribed: true },
    ]

    // Filter navigation based on auth and subscription status
    const visibleNavigation = navigation.filter(item => {
        if (item.hiddenIfSubscribed && isSubscribed) return false
        if (item.requiresAuth && !isLoggedIn) return false
        if (item.requiresSubscription && (!isLoggedIn || !isSubscribed)) return false
        return true
    })

  return (
    <Disclosure as="nav" className="bg-white shadow-sm dark:bg-brand-dark-900 transition-colors duration-200 border-b border-gray-200 dark:border-gray-700">
      {({ open, close }) => (
        <>
          <div className="mx-auto max-w-8xl px-2 sm:px-6 lg:px-8">
            <div className="relative flex h-16 justify-between">
              <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-red-500 dark:text-gray-300 dark:hover:bg-brand-dark-800 dark:hover:text-white">
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon aria-hidden="true" className="block size-6" />
                  ) : (
                    <Bars3Icon aria-hidden="true" className="block size-6" />
                  )}
                </DisclosureButton>
              </div>
              <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
                <div className="flex shrink-0 items-center">
                  <Link to="/">
                    <Logo className="h-13 w-auto transition-opacity duration-200" />
                  </Link>
                </div>
                <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                  {visibleNavigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={(e) => handleHashLink(e, item.href)}
                      aria-current={item.current ? 'page' : undefined}
                      className={classNames(
                        item.current ? 'border-brand-red-500 text-gray-900 dark:text-white' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:text-white',
                        'inline-flex items-center border-b-2 px-1 pt-1 font-medium relative'
                      )}
                    >
                      {item.name}
                      {item.badge && (
                        <span className="absolute top-5 -right-3 flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                {/* Theme Toggle Button - show on all views */}
                <ThemeToggle className="mr-2" />
                
                {isLoggedIn ? (
                  <>
                    <Menu as="div" className="relative ml-3 hidden sm:block">
                      <div>
                        <MenuButton className="relative flex rounded-full bg-white text-sm focus:outline-none dark:bg-brand-dark-800 hover:bg-gray-50 dark:hover:bg-brand-dark-700 transition-colors">
                          <span className="absolute -inset-1.5" />
                          <span className="sr-only">Open user menu</span>
                          <UserCircleIcon className="size-8 text-gray-600 dark:text-gray-300" />
                        </MenuButton>
                      </div>
                      <MenuItems
                        transition
                        className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in dark:bg-brand-dark-800 dark:ring-white/10"
                      >
                        <MenuItem>
                          {({ focus }) => (
                            <Link
                              to="/profile"
                              onClick={close}
                              className={classNames(focus ? 'bg-gray-100 dark:bg-brand-dark-700' : '', 'block px-4 py-2 text-sm text-gray-700 dark:text-gray-200')}
                            >
                              My Profile
                            </Link>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ focus }) => (
                            <button
                              onClick={() => {
                                handleLogout();
                                close();
                              }}
                              className={classNames(focus ? 'bg-gray-100 dark:bg-brand-dark-700' : '', 'block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200')}
                            >
                              Sign out
                            </button>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Menu>
                  </>
                ) : (
                  <div className="hidden sm:flex items-center space-x-2">
                    <SecondaryBtn
                      text="Log In"
                      onClick={() => {
                        navigate('/signin');
                        close();
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Add backdrop for mobile menu */}
          {open && (
            <div 
              className="fixed inset-0 bg-black/30 z-10 sm:hidden"
              aria-hidden="true"
              onClick={close}
            />
          )}

          <DisclosurePanel 
            static
            className={`sm:hidden overflow-hidden origin-top focus:outline-none z-20 bg-white dark:bg-brand-dark-900 fixed left-0 right-0 top-16 ${
              open
                ? 'opacity-100 scale-100 translate-y-0 max-h-[calc(100vh-4rem)]'
                : 'opacity-0 scale-95 -translate-y-2 max-h-0'
            } transition-all duration-200 ease-out`}
          >
            <div className="space-y-1 pt-2 pb-3">
              {isLoggedIn && (
                <>
                  {/* Systems and Games links first */}
                  {visibleNavigation
                    .filter(item => item.requiresSubscription)
                    .map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={(e) => {
                          handleHashLink(e, item.href);
                          close();
                        }}
                        aria-current={item.current ? 'page' : undefined}
                        className={classNames(
                          item.current ? 'border-brand-red-500 bg-brand-red-50 text-brand-red-700 dark:bg-brand-red-900/20 dark:text-brand-red-400' : 'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-brand-dark-800 dark:hover:text-white',
                          'block border-l-4 py-2 pl-3 pr-4 text-base font-medium'
                        )}
                      >
                        <span className="relative inline-flex items-center">
                          {item.name === 'Systems' ? (
                            <ChartBarIcon className="h-5 w-5 mr-2" />
                          ) : (
                            <TrophyIcon className="h-5 w-5 mr-2" />
                          )}
                          {item.name}
                          {item.badge && (
                            <span className="absolute -top-1 -right-3 flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                            </span>
                          )}
                        </span>
                      </Link>
                    ))}

                  {/* Profile and Sign out */}
                  <Link
                    to="/profile"
                    onClick={close}
                    className="block border-l-4 border-transparent py-2 pl-3 pr-4 text-base font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-brand-dark-800 dark:hover:text-white"
                  >
                    <span className="inline-flex items-center">
                      <UserCircleIcon className="h-5 w-5 mr-2" />
                      My Profile
                    </span>
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      close();
                    }}
                    className="block w-full border-l-4 border-transparent py-2 pl-3 pr-4 text-left text-base font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-brand-dark-800 dark:hover:text-white"
                  >
                    <span className="inline-flex items-center">
                      <ArrowRightEndOnRectangleIcon className="h-5 w-5 mr-2" />
                      Sign out
                    </span>
                  </button>
                </>
              )}
              
              {/* Other navigation items */}
              {visibleNavigation
                .filter(item => !item.requiresSubscription)
                .map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={(e) => {
                      handleHashLink(e, item.href);
                      close();
                    }}
                    aria-current={item.current ? 'page' : undefined}
                    className={classNames(
                      item.current ? 'border-brand-red-500 bg-brand-red-50 text-brand-red-700 dark:bg-brand-red-900/20 dark:text-brand-red-400' : 'border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-brand-dark-800 dark:hover:text-white',
                      'block border-l-4 py-2 pl-3 pr-4 text-base font-medium'
                    )}
                  >
                    <span className="relative inline-block">
                      {item.name}
                    </span>
                  </Link>
                ))}
            </div>
            
            {/* Mobile auth buttons */}
            {!isLoggedIn && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 pb-3">
                <div className="flex items-center justify-center space-x-2 px-4">
                  <button
                    onClick={() => {
                      navigate('/signin');
                      close();
                    }}
                    className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-brand-dark-800 dark:text-white dark:ring-brand-dark-600 dark:hover:bg-brand-dark-700"
                  >
                    Log In
                  </button>
                </div>
              </div>
            )}
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  )
}
